import clsx from 'clsx';
import {
  type ReactElement,
  type ReactNode,
  useMemo,
  Children,
  cloneElement,
} from 'react';
import { useIntl } from 'react-intl';

import {
  _mergeProps,
  type AriaLabelingProps,
  type DataTestId,
  type DOMProps,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

import { useTimeframeSelectorContext } from './hooks/useTimeframeSelectorContext.js';
import { PresetItem, type TimeframeSelectorPresetItemProps } from './Item.js';
import { timeframeSelectorPresetsCSS } from './TimeframeSelectorPresets.sty.js';
import { TIMEFRAME_SELECTOR_PRESETS } from './utils/timeframe-selection.js';
import { useRovingFocus } from '../../core/hooks/useRovingFocus.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { parseValue } from '../../forms/forms-core/time/timeParser.js';

//#region Typings
/**
 * @public
 */
export interface TimeframeSelectorPresetsProps
  extends DOMProps,
    DataTestId,
    StylingProps,
    AriaLabelingProps {
  /** Children shown inside the presets list.A default list of presets is shown if no children are set. */
  children?: ReactNode;
}

interface TimeframeSelectorPresetsSlots {
  items:
    | ReactElement<
        TimeframeSelectorPresetItemProps & { _isSelected?: boolean }
      >[]
    | null;
}
//#endregion

/** Iterates over child nodes to extract specific component slots. */
function getItemSlotsAndSelectedIndex(
  children: ReactNode,
  from?: string,
  to?: string,
  precision?: 'minutes' | 'seconds' | 'milliseconds',
) {
  const slots: TimeframeSelectorPresetsSlots = {
    items: null,
  };
  let selectedPresetIndex = 0;

  if (!children) {
    return { ...slots, selectedPresetIndex };
  }

  for (const [index, child] of Children.toArray(children).entries()) {
    if (isSlot(child, PresetItem)) {
      const childProps = child.props;
      const { from: propFrom, to: propTo } = parseValue(
        childProps.value,
        false,
        precision,
      );

      const isSelected =
        from && from === propFrom?.value && to && to === propTo?.value;
      slots.items = slots.items || [];

      if (isSelected) {
        selectedPresetIndex = index;
        slots.items.push(
          cloneElement<
            TimeframeSelectorPresetItemProps & { _isSelected?: boolean }
          >(child, {
            ..._mergeProps(child.props, { _isSelected: true }),
          }),
        );
      } else {
        slots.items.push(child);
      }
    } else {
      // do nothing if the child is null (when rendered conditionally for example) and throw a warning otherwise
      // eslint-disable-next-line no-console
      console.warn(
        'The used child is not supported by the TimeframeSelector.Presets.',
      );
    }
  }

  return { ...slots, selectedPresetIndex };
}

/**
 * A list of preset items shown in the overlay.
 * @public
 */
export const Presets = (props: TimeframeSelectorPresetsProps) => {
  const {
    children,
    'aria-label': ariaLabel,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  }: TimeframeSelectorPresetsProps = props;
  const intl = useIntl();

  const { details, precision } = useTimeframeSelectorContext();
  const from = details?.from?.value;
  const to = details?.to?.value;

  const selectedDefaultPresetIndex = useMemo(
    () =>
      TIMEFRAME_SELECTOR_PRESETS.findIndex(
        (preset) => preset.from === from && preset.to === to,
      ),
    [from, to],
  );

  const { items, selectedPresetIndex } = useMemo(
    () => getItemSlotsAndSelectedIndex(children, from, to, precision),
    [children, from, to, precision],
  );

  const { containerRef } = useRovingFocus<HTMLUListElement>({
    initialIndex: items ? selectedPresetIndex : selectedDefaultPresetIndex,
    direction: 'vertical',
    focusableChildrenSelector: "[role='option'][aria-disabled='false']",
  });

  const presetsListLabel =
    ariaLabel ??
    intl.formatMessage({
      defaultMessage: 'List of timeframe presets',
      id: 'v8C2Lvq2DZvfWbNV',
      description: 'Aria label for the list of timeframe presets',
    });

  return (
    <Flex
      paddingLeft={0}
      paddingRight={0}
      flexDirection="column"
      style={{ overflowY: 'auto' }}
    >
      <ul
        ref={containerRef}
        role="listbox"
        aria-label={presetsListLabel}
        className={clsx([timeframeSelectorPresetsCSS, consumerClassName])}
        style={consumerStyle}
        data-testid={dataTestId}
        {...remainingProps}
      >
        {items ??
          TIMEFRAME_SELECTOR_PRESETS.map((item, index) => (
            <PresetItem
              key={`${item.from} → ${item.to}`}
              value={item}
              _isSelected={selectedDefaultPresetIndex === index}
            />
          ))}
      </ul>
    </Flex>
  );
};

Presets['displayName'] = 'TimeframeSelector.Presets';
